import React from 'react';

/**
 * Hook for handling block events (tracking and interaction events)
 * Consolidates all event-related logic into a reusable hook
 */
export const useBlockEvents = (blockSettings, defaultEventLabel = '') => {
    // Event tracking settings
    const enableEventTracking = blockSettings.enable_event_tracking || false;
    const eventName = blockSettings.event_name || 'block_interaction';
    const eventCategory = blockSettings.event_category || 'interaction';
    const eventLabel = blockSettings.event_label || defaultEventLabel;
    const eventValue = blockSettings.event_value ? Number(blockSettings.event_value) : undefined;
    
    // Interaction event settings
    const enableInteractionEvents = blockSettings.enable_interaction_events || false;
    const interactionEventId = blockSettings.interaction_event_id || '';
    const interactionType = blockSettings.interaction_type || 'click';

    // Action event settings
    const actionEventId = blockSettings.interaction_event_id || ''; // Support both naming conventions
    const enableActionEvents = actionEventId !== '' || blockSettings.enable_interaction_events || false; // Auto-enable if event_id is present
    const actionTrigger = blockSettings.action_trigger || 'click'; // click, hover, focus, etc.
    const actionData = blockSettings.action_data || {};
    
    // Function to dispatch interaction events
    const dispatchInteractionEvent = (eventType, action = 'trigger', data = {}) => {
        if (!enableInteractionEvents || !interactionEventId) return;
        
        const customEvent = new CustomEvent('interaction-event', {
            detail: {
                id: interactionEventId,
                type: interactionType,
                eventType,
                action,
                timestamp: Date.now(),
                ...data
            }
        });
        
        window.dispatchEvent(customEvent);
    };
    
    // Function to dispatch action events
    const dispatchActionEvent = (triggerType, customData = {}) => {
      
        if (!enableActionEvents || !actionEventId) return;
        
        // Only trigger if the trigger type matches the configured trigger
        if (actionTrigger !== triggerType) return;
        
        console.log(`🔔 Dispatching action event for trigger:`, customData, eventName);
      
        const eventData = {
            ...actionData,
            ...customData,
            triggerType,
            blockSettings: {
                block_id: blockSettings.block_id,
                event_label: eventLabel
            }
        };
        
        console.log(`🚀 Dispatching action event: ${eventName}`, eventData);
        
        const actionEvent = new CustomEvent(eventName, {
            detail: eventData
        });
        
        window.dispatchEvent(actionEvent);
    };
    
    // Function to dispatch analytics events
    const dispatchAnalyticsEvent = (customEventName = null, customData = {}) => {
        if (!enableEventTracking) return;
        
        const eventData = {
            event: customEventName || eventName,
            event_category: eventCategory,
            event_label: eventLabel,
            ...(eventValue !== undefined && { event_value: eventValue }),
            ...customData
        };
        
        // Dispatch to dataLayer for Google Analytics
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push(eventData);
        }
        
        // Dispatch custom event for other tracking systems
        const customEvent = new CustomEvent('analytics-event', {
            detail: eventData
        });
        window.dispatchEvent(customEvent);
    };
     // Generic event handlers
    const handleClick = (customData = {}) => {
        // Prevent double firing with a simple debounce
        const now = Date.now();
        if (handleClick._lastCall && (now - handleClick._lastCall) < 100) {
            // console.warn('🚫 Prevented double click event firing');
            return;
        }
        handleClick._lastCall = now;
        
        // console.log('🔥 Event fired: click', { eventName, customData, enableEventTracking, enableInteractionEvents, enableActionEvents });
        
        if (enableEventTracking) {
            dispatchAnalyticsEvent(`${eventName}_click`, customData);
        }
        if (enableInteractionEvents) {
            dispatchInteractionEvent('click', 'trigger', customData);
        }
        if (enableActionEvents) {
            dispatchActionEvent('click', customData);
        }
    };

    const handleMouseEnter = (customData = {}) => {
        // Prevent double firing with a simple debounce
        const now = Date.now();
        if (handleMouseEnter._lastCall && (now - handleMouseEnter._lastCall) < 50) {
            return;
        }
        handleMouseEnter._lastCall = now;
        
        // console.log('🔥 Event fired: mouse enter', { eventName, customData });
        if (enableEventTracking) {
            dispatchAnalyticsEvent(`${eventName}_hover_enter`, customData);
        }
        if (enableInteractionEvents) {
            dispatchInteractionEvent('hover', 'enter', customData);
        }
        if (enableActionEvents) {
            dispatchActionEvent('hover', customData);
        }
    };

    const handleMouseLeave = (customData = {}) => {
        // Prevent double firing with a simple debounce
        const now = Date.now();
        if (handleMouseLeave._lastCall && (now - handleMouseLeave._lastCall) < 50) {
            return;
        }
        handleMouseLeave._lastCall = now;
        
        // console.log('🔥 Event fired: mouse leave', { eventName, customData });
        if (enableEventTracking) {
            dispatchAnalyticsEvent(`${eventName}_hover_leave`, customData);
        }
        if (enableInteractionEvents) {
            dispatchInteractionEvent('hover', 'leave', customData);
        }
        if (enableActionEvents) {
            dispatchActionEvent('hover', customData);
        }
    };
    
    const handleFocus = (customData = {}) => {
        if (enableEventTracking) {
            dispatchAnalyticsEvent(`${eventName}_focus`, customData);
        }
        if (enableInteractionEvents) {
            dispatchInteractionEvent('focus', 'trigger', customData);
        }
        if (enableActionEvents) {
            dispatchActionEvent('focus', customData);
        }
    };
    
    const handleBlur = (customData = {}) => {
        if (enableEventTracking) {
            dispatchAnalyticsEvent(`${eventName}_blur`, customData);
        }
        if (enableInteractionEvents) {
            dispatchInteractionEvent('blur', 'trigger', customData);
        }
        if (enableActionEvents) {
            dispatchActionEvent('blur', customData);
        }
    };
    
    const handleChange = (value, customData = {}) => {
        if (enableEventTracking) {
            dispatchAnalyticsEvent(`${eventName}_change`, { value, ...customData });
        }
        if (enableInteractionEvents) {
            dispatchInteractionEvent('change', 'trigger', { value, ...customData });
        }
        if (enableActionEvents) {
            dispatchActionEvent('change', { value, ...customData });
        }
    };
    
    return {
        // Settings
        enableEventTracking,
        enableInteractionEvents,
        enableActionEvents,
        
        // Event dispatchers
        dispatchInteractionEvent,
        dispatchAnalyticsEvent,
        dispatchActionEvent,
        
        // Common handlers
        handleClick,
        handleMouseEnter,
        handleMouseLeave,
        handleFocus,
        handleBlur,
        handleChange,
        
        // Event data
        eventData: {
            eventName,
            eventCategory,
            eventLabel,
            eventValue,
            interactionEventId,
            interactionType,
            actionEventId,
            actionTrigger,
            actionData
        }
    };
};
